import { useRef, useEffect, useCallback, useState } from 'react';
import styles from './MaterialPreviewModal.module.css';
import { TagSelector } from '../../tags';
import { normalizeAsinForDisplay } from './downloadUtils';
import { formatContentType, formatFileSizeLabel, isSvgContentType } from './materialUtils';
import type { MaterialData } from './types';
import type { Tag } from '../../../features/tags/types';
import type { TemporaryMaterial, CloudMaterial } from '../../../features/materials/materialsSlice';
import type { MaterialScenario, ProductLink } from '../../../features/materials/api';
import type { PromptModel } from '../../generation/hooks/usePromptModels';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { useCoachMarkDismiss } from '../../../hooks/useCoachMarkDismiss';
import { brandConfig } from '../../../config/brands';
import { labelMaterialSource } from '../../../features/materials/sourceLabels';
import { GenerationInfoButton } from './GenerationInfoButton';
import { useMaterialWorkflowStages } from '../../../features/materials/hooks';
import { CompliancePanel } from './CompliancePanel';
import { appPath } from '../../../utils/navigation';
import { useImageZoom } from './useImageZoom';

// ProductLinksDisplay component (shared)
export type ProductLinkDisplayItem = {
  id?: string;
  product_type: 'amazon' | 'manual';
  product_id: string;
  product_title?: string;
  asin?: string;
  title?: string; // alias for product_title
  // 削除済 manual product を指している場合 true (backend enrichment 由来)
  product_deleted?: boolean;
};

export function ProductLinksDisplay({
  productLinks,
  navigate,
}: {
  productLinks: ProductLinkDisplayItem[];
  navigate?: (path: string) => void;
}) {
  if (productLinks.length === 0) return <span>-</span>;

  const MAX_DISPLAY = 20;
  const displayLinks = productLinks.slice(0, MAX_DISPLAY);
  const hiddenCount = productLinks.length - displayLinks.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden' }}>
      {displayLinks.map((link, index) => {
        const isAmazon = link.product_type === 'amazon';
        const isDeleted = link.product_deleted === true;
        const displayTitle = isDeleted
          ? '(削除済みプロジェクト)'
          : (link.product_title || link.title || link.asin || '(無題)');
        const href = isAmazon
          ? `/products/amazon/${link.asin || link.product_id}`
          : `/products/manual/${link.product_id}`;
        const key = link.id || `${link.product_type}:${link.product_id}:${index}`;

        const badge = (
          <span
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '18px',
              height: '18px',
              borderRadius: '3px',
              fontSize: '0.6rem',
              fontWeight: 700,
              lineHeight: 1,
              background: isAmazon ? 'rgba(var(--brand-primary-rgb) / 0.2)' : 'rgba(180, 200, 230, 0.2)',
              color: isAmazon ? 'rgb(220, 190, 100)' : 'rgb(180, 200, 230)',
              opacity: isDeleted ? 0.5 : 1,
            }}
            title={isAmazon ? 'Amazon' : 'マニュアル'}
          >
            {isAmazon ? 'A' : 'M'}
          </span>
        );

        const textSpan = (
          <span
            data-link-text=""
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontStyle: isDeleted ? 'italic' : undefined,
              color: isDeleted ? 'rgba(245, 241, 230, 0.5)' : undefined,
            }}
          >
            {displayTitle}
          </span>
        );

        // 削除済みリンクはクリック不可 (遷移しても 404)
        if (isDeleted) {
          return (
            <span
              key={key}
              title={displayTitle}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'rgba(245, 241, 230, 0.5)',
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              {badge}
              {textSpan}
            </span>
          );
        }

        if (navigate) {
          return (
            <a
              key={key}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(href);
              }}
              title={displayTitle}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'rgba(245, 241, 230, 0.85)',
                textDecoration: 'none',
                cursor: 'pointer',
                overflow: 'hidden',
                minWidth: 0,
              }}
              onMouseEnter={(e) => {
                const textEl = e.currentTarget.querySelector('[data-link-text]') as HTMLElement | null;
                if (textEl) textEl.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                const textEl = e.currentTarget.querySelector('[data-link-text]') as HTMLElement | null;
                if (textEl) textEl.style.textDecoration = 'none';
              }}
            >
              {badge}
              {textSpan}
            </a>
          );
        }

        return (
          <span
            key={key}
            title={displayTitle}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: 'rgba(245, 241, 230, 0.85)',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            {badge}
            {textSpan}
          </span>
        );
      })}
      {hiddenCount > 0 && (
        <span style={{ fontSize: '0.75rem', color: 'rgba(245, 241, 230, 0.5)' }}>
          …他 {hiddenCount} 件
        </span>
      )}
    </div>
  );
}

// ScenarioLinks component (helper)
function ScenarioLinks({
  scenarios,
}: {
  scenarios?: MaterialScenario[];
}) {
  if (!scenarios || scenarios.length === 0) return <span>-</span>;

  const handleScenarioClick = (scenarioId: string) => {
    // モーダルを閉じてからシナリオページに遷移
    // URLパラメータをクリアしてシナリオIDのみを設定
    // VITE_APP_BASENAME=/app などサブパス配信時に basename を取りこぼさないよう appPath を経由する
    const url = new URL(appPath('/'), window.location.origin);
    url.searchParams.set('tab', 'scenario');
    url.searchParams.set('scenario_id', scenarioId);
    window.location.href = url.toString();
  };

  return (
    <>
      {scenarios.map((scenario, index) => (
        <span key={scenario.id}>
          {index > 0 && ', '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleScenarioClick(scenario.id);
            }}
            style={{
              color: 'rgb(220, 190, 100)',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
            title={scenario.title}
          >
            {scenario.title}
          </a>
        </span>
      ))}
    </>
  );
}

function getSourceLabel(material: { source_type?: string | null; default_audio_role?: string | null }): string {
  if (!material.source_type) return '不明';
  // 取得元ラベルは共有マップ (features/materials/sourceLabels.ts) を唯一の正本とする。
  // モーダル固有のオーバーライドは持たない。新しい source_type はそちらに追加する。
  return labelMaterialSource(material);
}

interface MaterialPreviewViewProps {
  material: MaterialData;
  materialId?: string;
  isVideo: boolean;
  isAudio?: boolean;
  fullSizeImageUrl: string | null;
  videoUrl: string | null;
  audioUrl?: string | null;
  /** GCS署名付きURL取得失敗時のメッセージ（動画・音声の再生URLが解決できなかった場合に表示） */
  mediaLoadError?: string | null;
  showEditControls: boolean;
  tags: Tag[];
  notes: string;
  isNotesDirty: boolean;
  materialName: string;
  isMaterialNameDirty: boolean;
  updateMutation: any;
  onNavigate?: (direction: 1 | -1) => void;
  onTagsChange: (tags: Tag[]) => void;
  onNotesChange: (notes: string) => void;
  onNotesCancel: () => void;
  onNotesSave: () => void;
  onMaterialNameChange: (name: string) => void;
  onMaterialNameCancel: () => void;
  onMaterialNameSave: () => void;
  onWorkflowStageChange?: (stageId: string | null) => void;
  openProductSelector: () => void;
  navigate: (path: string) => void;
  actorModels?: PromptModel[];
  productLinks?: ProductLink[];
  isProductLinksLoading?: boolean;
  isScenariosLoading?: boolean;
  isMobile?: boolean;
  isImageLoading?: boolean;
  onImageLoadComplete?: () => void;
  hasGenerationInfo?: boolean;
  onShowGenerationInfo?: () => void;
  /** サニタイズファイル (実在人物フィルタ対策の覆面化/線画の隠しファイル) の表示用 URL。
   *  紐付けがある画像素材のみ渡され、プレビューに表示切り替えトグルを出す。 */
  facePrivacyTwinUrl?: string | null;
  /** サニタイズファイルのスタイル (トグルの文言出し分け)。既定は覆面化 */
  facePrivacyTwinStyle?: 'faceless' | 'line_art';
}

export function MaterialPreviewView({
  material,
  isVideo,
  isAudio,
  fullSizeImageUrl,
  videoUrl,
  audioUrl,
  mediaLoadError,
  showEditControls,
  tags,
  notes,
  isNotesDirty,
  materialName,
  isMaterialNameDirty,
  updateMutation,
  onNavigate,
  onTagsChange,
  onNotesChange,
  onNotesCancel,
  onNotesSave,
  onMaterialNameChange,
  onMaterialNameCancel,
  onMaterialNameSave,
  onWorkflowStageChange,
  openProductSelector,
  navigate,
  actorModels,
  productLinks = [],
  isProductLinksLoading,
  isScenariosLoading,
  isMobile,
  isImageLoading,
  onImageLoadComplete,
  hasGenerationInfo,
  onShowGenerationInfo,
  facePrivacyTwinUrl,
  facePrivacyTwinStyle = 'faceless',
}: MaterialPreviewViewProps) {
  const isSvg = isSvgContentType(material.content_type);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const productLinkButtonRef = useRef<HTMLButtonElement>(null);
  const imagePaneRef = useRef<HTMLDivElement>(null);

  // 覆面化ファイルの表示トグル。素材を切り替えたら元画像表示へ戻す
  const [showFacePrivacyTwin, setShowFacePrivacyTwin] = useState(false);
  useEffect(() => {
    setShowFacePrivacyTwin(false);
  }, [material.id]);

  // 画像のズーム (動画・音声・読み込みエラー表示中は無効)。表示中の画像が変わったら全体表示に戻す
  const imageRef = useRef<HTMLImageElement | null>(null);
  const showsMediaError = (isVideo || !!isAudio) && !videoUrl && !audioUrl && !!mediaLoadError;
  const showsImage = !!fullSizeImageUrl && !showsMediaError && !(isAudio && audioUrl) && !(isVideo && videoUrl);
  const shownImageUrl = showFacePrivacyTwin && facePrivacyTwinUrl ? facePrivacyTwinUrl : fullSizeImageUrl;
  const zoom = useImageZoom({ paneRef: imagePaneRef, imgRef: imageRef, enabled: showsImage, resetKey: shownImageUrl });
  const isZoomedRef = zoom.isZoomedRef;

  // 制作進捗 (workflow stage) — 組織で有効な場合、個人/共有フォルダ問わず表示
  const workflowStagesQuery = useMaterialWorkflowStages();
  const workflowStages = workflowStagesQuery.data ?? [];
  const showWorkflowStage = workflowStages.length > 0;
  const currentStageId = (material as CloudMaterial | TemporaryMaterial).workflow_stage_id ?? null;

  // 横スワイプ状態
  const [swipeOffsetX, setSwipeOffsetX] = useState(0);
  const [swipeTransition, setSwipeTransition] = useState(false);

  // モバイル横スワイプ
  useEffect(() => {
    if (!isMobile || !onNavigate) return;
    const el = imagePaneRef.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let isVerticalScroll = false;
    let directionDecided = false;
    let currentDeltaX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      // 拡大中の 1 本指ドラッグは表示位置の移動に使う
      if (e.touches.length !== 1 || isZoomedRef.current) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
      isVerticalScroll = false;
      directionDecided = false;
      currentDeltaX = 0;
      setSwipeTransition(false);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1 || isZoomedRef.current) return;
      const deltaX = e.touches[0].clientX - startX;
      const deltaY = e.touches[0].clientY - startY;

      if (!directionDecided && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
        directionDecided = true;
        isVerticalScroll = Math.abs(deltaY) > Math.abs(deltaX);
      }

      if (directionDecided && !isVerticalScroll) {
        e.preventDefault();
        currentDeltaX = deltaX;
        setSwipeOffsetX(deltaX);
      }
    };

    const handleTouchEnd = () => {
      if (isVerticalScroll || !directionDecided || isZoomedRef.current) {
        setSwipeOffsetX(0);
        return;
      }

      const elapsed = Date.now() - startTime;
      const velocity = Math.abs(currentDeltaX) / Math.max(elapsed, 1);

      if (Math.abs(currentDeltaX) >= 50 || velocity >= 0.3) {
        // スライドアウトアニメーション
        setSwipeTransition(true);
        setSwipeOffsetX(currentDeltaX > 0 ? window.innerWidth : -window.innerWidth);
        setTimeout(() => {
          onNavigate(currentDeltaX > 0 ? -1 : 1);
          setSwipeOffsetX(0);
          setSwipeTransition(false);
        }, 200);
      } else {
        // スナップバック
        setSwipeTransition(true);
        setSwipeOffsetX(0);
        setTimeout(() => setSwipeTransition(false), 250);
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, onNavigate, isZoomedRef]);



  // 商品紐づけコーチマーク
  const showProductLinkCoachMark = useMaterialsPanelStore((s) => s.showProductLinkCoachMark);
  const dismissProductLinkCoachMark = useMaterialsPanelStore((s) => s.dismissProductLinkCoachMark);
  useCoachMarkDismiss(productLinkButtonRef, showProductLinkCoachMark, dismissProductLinkCoachMark);

  // テキストエリアの高さを内容に合わせて自動調整
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 一旦高さをリセットしてスクロール高さを取得
      textarea.style.height = 'auto';
      // 最小1行（約38px）、最大は制限なし
      const newHeight = Math.max(38, textarea.scrollHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  // notes が変更されたら高さを調整
  useEffect(() => {
    adjustTextareaHeight();
  }, [notes, adjustTextareaHeight]);

  // 簡素素材 UI (usodeki 等) ではメタ情報ペインを出さず画像のみのカードにする
  const simpleMaterialsUi = brandConfig.features.simpleMaterialsUi ?? false;
  return (
    <div className={`${styles.content}${simpleMaterialsUi ? ` ${styles.contentNoMeta}` : ''}`}>
      <div
        className={`${styles.imagePane}${zoom.isZoomed ? ` ${styles.imagePaneZoomed}` : ''}${zoom.isDragging ? ` ${styles.imagePaneDragging}` : ''}`}
        ref={imagePaneRef}
        {...zoom.paneHandlers}
      >
        {isImageLoading && (
          <div className={styles.imageLoadingOverlay} />
        )}
        {onNavigate && (
          <button type="button" className={styles.navButton} onClick={() => onNavigate(-1)} aria-label="前の素材">
            ‹
          </button>
        )}
        <div
          className={`${styles.imagePaneContent}${swipeTransition ? ` ${styles.swipeTransition}` : ''}`}
          style={swipeOffsetX !== 0 ? { transform: `translateX(${swipeOffsetX}px)`, opacity: Math.max(0.3, 1 - Math.abs(swipeOffsetX) / window.innerWidth) } : undefined}
        >
          {showsMediaError ? (
            // 動画・音声の再生URLが解決できなかった場合、サムネイルに無言でフォールバックせず
            // 明示的にエラーを表示する（多くはGCS署名付きURLの取得失敗＝ローカル認証切れ）
            <div className={styles.mediaError} role="alert">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div className={styles.mediaErrorTitle}>{isAudio ? '音声を読み込めませんでした' : '動画を読み込めませんでした'}</div>
              <div className={styles.mediaErrorDetail}>{mediaLoadError}</div>
              <div className={styles.mediaErrorHint}>
                署名付きURLの生成に失敗しています。ローカル開発では GCS 認証 (ADC) 切れの可能性があります（
                <code>gcloud auth application-default login</code> を実行してバックエンドを再起動）。
              </div>
            </div>
          ) : isAudio && audioUrl ? (
            <div className={styles.audioPreview}>
              {(material.generation_metadata?.narration_text || material.generation_metadata?.song_title) ? (
                <div className={styles.audioNarrationText}>
                  {(material.generation_metadata?.narration_text || material.generation_metadata?.song_title) as string}
                </div>
              ) : (
                <div className={styles.audioIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18V5l12-2v13" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="18" cy="16" r="3" />
                  </svg>
                </div>
              )}
              <audio src={audioUrl} controls style={{ width: '80%', maxWidth: '400px' }} />
            </div>
          ) : fullSizeImageUrl ? (
            isVideo && videoUrl ? (
              <video src={videoUrl} controls className={styles.videoPreview} />
            ) : (
              // 覆面化ファイル表示中は隠しファイル (覆面化画像) を出す
              <img
                key={shownImageUrl}
                src={shownImageUrl ?? undefined}
                alt={normalizeAsinForDisplay(material.asin) || 'プレビュー'}
                draggable={false}
                className={zoom.isDragging ? undefined : styles.zoomableImage}
                style={zoom.imageTransform ? { transform: zoom.imageTransform } : undefined}
                onLoad={() => {
                  zoom.measure();
                  onImageLoadComplete?.();
                }}
                ref={(img) => {
                  imageRef.current = img;
                  // キャッシュ済み画像はonLoadが発火しないことがあるのでcompleteチェック
                  if (img?.complete) {
                    zoom.measure();
                    onImageLoadComplete?.();
                  }
                }}
              />
            )
          ) : (
            <div className={styles.placeholder}>プレビュー画像がありません</div>
          )}
        </div>
        {showsImage && (
          <div className={styles.previewZoomControls}>
            <button type="button" onClick={zoom.zoomOut} disabled={zoom.scale <= zoom.minScale} aria-label="縮小" title="縮小 (-)">
              −
            </button>
            <button
              type="button"
              className={styles.previewZoomLevel}
              onClick={zoom.resetZoom}
              disabled={zoom.isFit}
              title="原寸に対する表示倍率。クリックで全体表示に戻す (0)・画像のダブルクリックで2倍ずつ拡大、Ctrl+ホイールやピンチでも拡大縮小できます"
            >
              {zoom.actualPercent}%
            </button>
            <button type="button" onClick={zoom.zoomIn} disabled={zoom.scale >= zoom.maxScale} aria-label="拡大" title="拡大 (+)">
              +
            </button>
          </div>
        )}
        {facePrivacyTwinUrl && !isVideo && !isAudio && fullSizeImageUrl && (() => {
          const twinLabel = facePrivacyTwinStyle === 'line_art' ? '線画' : '覆面化';
          return (
            <button
              type="button"
              className={styles.facePrivacyTwinToggle}
              title={`実在人物フィルタ対策で作成した${twinLabel}画像 (隠しファイル) の表示を切り替えます`}
              onClick={() => setShowFacePrivacyTwin((v) => !v)}
            >
              {showFacePrivacyTwin ? '元のファイルを表示' : `${twinLabel}ファイルを表示`}
            </button>
          );
        })()}
        {onNavigate && (
          <button type="button" className={`${styles.navButton} ${styles.navButtonNext}`} onClick={() => onNavigate(1)} aria-label="次の素材">
            ›
          </button>
        )}
      </div>
      {/* usodeki は関連商品・関連シナリオ・タグ・メモ等のメタ情報パネルを一切出さない */}
      {!simpleMaterialsUi && (
      <aside className={styles.metaPane}>
        <dl>
          {/* スマホ用: 基本情報をコンパクトに1行で表示 */}
          <div className={styles.metaCompact}>
            <span>{formatContentType(material.content_type) || (isVideo ? '動画' : '画像')}</span>
            {!isSvg && <span>{isAudio && material.duration_seconds
              ? `${Math.round(material.duration_seconds)}秒`
              : material.width && material.height ? `${material.width}×${material.height}` : '不明'}</span>}
            <span>{formatFileSizeLabel(material.file_size_bytes)}</span>
            <span>{(() => {
              const dateStr = material.created_at || (material as TemporaryMaterial).createdAt;
              if (!dateStr) return '-';
              try {
                const date = new Date(dateStr);
                return date.toLocaleString('ja-JP', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                }).replace(/\//g, '-');
              } catch {
                return dateStr;
              }
            })()}</span>
          </div>

          <div style={{ position: 'relative' }}>
            <dt>関連{brandConfig.labels.productShort}</dt>
            <dd style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                {isProductLinksLoading ? (
                  <span className={styles.metaSpinner} />
                ) : (
                  <ProductLinksDisplay
                    productLinks={productLinks}
                    navigate={navigate}
                  />
                )}
              </span>
              {showEditControls && (
                <button
                  ref={productLinkButtonRef}
                  type="button"
                  onClick={openProductSelector}
                  disabled={updateMutation.isPending}
                  className="btn-pill-outline"
                  title={`${brandConfig.labels.productShort}リストから選択、またはASIN直接入力`}
                >
                  {brandConfig.labels.productShort}と関連付ける
                </button>
              )}
            </dd>
            {showEditControls && showProductLinkCoachMark && !isProductLinksLoading && productLinks.length === 0 && (
              <div
                className="coach-mark"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 10px)',
                  left: '0',
                  right: '0',
                  padding: '12px 16px',
                  zIndex: 1120,
                }}
                onClick={dismissProductLinkCoachMark}
              >
                <span style={{
                  position: 'absolute',
                  top: '-6px',
                  left: '24px',
                  width: '12px',
                  height: '12px',
                  background: 'rgba(var(--brand-primary-rgb) / 0.95)',
                  transform: 'rotate(45deg)',
                  borderRadius: '2px',
                }} />
                こちらから{brandConfig.labels.productShort}を関連付けてください
                <br />
                <span style={{ fontSize: '12px', opacity: 0.85 }}>
                  未登録の{brandConfig.labels.productShort}情報の追加は
                  <a
                    href="/products/manual"
                    style={{ color: '#fff', textDecoration: 'underline' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      dismissProductLinkCoachMark();
                      navigate('/products/manual');
                    }}
                  >
                    こちら
                  </a>
                </span>
              </div>
            )}
          </div>
          <div>
            <dt>関連シナリオ</dt>
            <dd>
              {isScenariosLoading ? (
                <span className={styles.metaSpinner} />
              ) : (
                <ScenarioLinks
                  scenarios={material.relatedScenarios}
                />
              )}
            </dd>
          </div>

          {/* PC用: 基本情報を個別に表示 */}
          <div className={styles.metaBasicInfo}>
            <dt>取得元</dt>
            <dd className={styles.sourceWithGenInfo}>
              {getSourceLabel(material)}
              {hasGenerationInfo && onShowGenerationInfo && (
                <GenerationInfoButton onClick={onShowGenerationInfo} />
              )}
            </dd>
          </div>
          <div className={styles.metaBasicInfo}>
            <dt>コンテンツ種別</dt>
            <dd>{formatContentType(material.content_type) || (isVideo ? '動画' : '画像')}</dd>
          </div>
          {/* SVG はベクターなので幅・高さは出さない */}
          {!isSvg && (
          <div className={styles.metaBasicInfo}>
            <dt>サイズ</dt>
            <dd>{isAudio && material.duration_seconds
              ? `${Math.round(material.duration_seconds)}秒`
              : material.width && material.height ? `${material.width}×${material.height}` : '不明'}</dd>
          </div>
          )}
          <div className={styles.metaBasicInfo}>
            <dt>ファイル容量</dt>
            <dd>{formatFileSizeLabel(material.file_size_bytes)}</dd>
          </div>
          <div className={styles.metaBasicInfo}>
            <dt>作成日時</dt>
            <dd>{(() => {
              const dateStr = material.created_at || (material as TemporaryMaterial).createdAt;
              if (!dateStr) return '-';
              try {
                const date = new Date(dateStr);
                return date.toLocaleString('ja-JP', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false,
                }).replace(/\//g, '-');
              } catch {
                return dateStr;
              }
            })()}</dd>
          </div>
        </dl>
        {showEditControls && 'notes' in material && (
          <>
            <div className={styles.editSection}>
              <div className={styles.infoGroup}>
                <span className={styles.infoLabel}>{brandConfig.labels.tag}</span>
                <TagSelector value={tags} onChange={onTagsChange} disabled={updateMutation.isPending} />
              </div>
              {showWorkflowStage && onWorkflowStageChange && (
                <div className={styles.infoGroup}>
                  <span className={styles.infoLabel}>制作進捗</span>
                  <select
                    value={currentStageId ?? ''}
                    onChange={(e) => onWorkflowStageChange(e.target.value ? e.target.value : null)}
                    disabled={updateMutation.isPending}
                    className={`${styles.notesTextarea} ${styles.notesSelect}`}
                    style={{ padding: '6px 8px' }}
                  >
                    <option value="">未設定</option>
                    {workflowStages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className={`${styles.infoGroup} ${styles.genInfoButtonMobile}`}>
                <span className={styles.infoLabel}>取得元</span>
                <span className={styles.infoValueSmall}>{getSourceLabel(material)}</span>
                {hasGenerationInfo && onShowGenerationInfo && (
                  <GenerationInfoButton onClick={onShowGenerationInfo} />
                )}
              </div>
              {(() => {
                // コンプラ判定は AI 生成画像にのみ表示
                if (material.source_type !== 'generated') return null;
                const gm = (material as unknown as { generation_metadata?: Record<string, unknown> }).generation_metadata;
                const appMeta = gm && typeof gm === 'object'
                  ? ((gm as Record<string, unknown>)._app as Record<string, unknown> | undefined)
                  : undefined;
                const complianceMeta = appMeta?.compliance as Record<string, unknown> | undefined;
                const industries = Array.isArray(complianceMeta?.industry_categories)
                  ? (complianceMeta.industry_categories as unknown[]).filter((v): v is string => typeof v === 'string')
                  : [];
                const appliedRaw = Array.isArray(complianceMeta?.applied_rulesets)
                  ? (complianceMeta.applied_rulesets as Array<Record<string, unknown>>)
                  : [];
                const appliedRulesets = appliedRaw.map((a) => ({
                  code: typeof a.code === 'string' ? a.code : '',
                  name: typeof a.name === 'string' ? a.name : (typeof a.code === 'string' ? a.code : ''),
                  severity: typeof a.severity === 'string' ? a.severity : undefined,
                }));
                const materialTable: 'temporary_materials' | 'materials' =
                  material.folder_type === 'cloud' ? 'materials' : 'temporary_materials';
                return (
                  <div className={styles.infoGroup}>
                    <span className={styles.infoLabel}>コンプラ判定</span>
                    <CompliancePanel
                      materialId={material.id}
                      materialTable={materialTable}
                      appliedRulesets={appliedRulesets}
                      hasIndustryCategories={industries.length > 0}
                    />
                  </div>
                );
              })()}
              <div className={styles.infoGroup}>
                <span className={styles.infoLabel}>名前</span>
                <input
                  type="text"
                  value={materialName}
                  onChange={(event) => onMaterialNameChange(event.target.value)}
                  placeholder="素材の名前"
                  maxLength={200}
                  disabled={updateMutation.isPending}
                  className={styles.notesTextarea}
                />
                {isMaterialNameDirty && (
                  <div className={styles.notesActions}>
                    <button
                      type="button"
                      onClick={onMaterialNameCancel}
                      disabled={updateMutation.isPending}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      onClick={onMaterialNameSave}
                      disabled={updateMutation.isPending}
                    >
                      保存
                    </button>
                  </div>
                )}
              </div>
              <div className={styles.infoGroup}>
                <span className={styles.infoLabel}>メモ</span>
                <textarea
                  ref={textareaRef}
                  value={notes}
                  onChange={(event) => onNotesChange(event.target.value)}
                  rows={1}
                  placeholder="メモを自由に記述"
                  disabled={updateMutation.isPending}
                  className={styles.notesTextarea}
                />
                {isNotesDirty && (
                  <div className={styles.notesActions}>
                    <button
                      type="button"
                      onClick={onNotesCancel}
                      disabled={updateMutation.isPending}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      onClick={onNotesSave}
                      disabled={updateMutation.isPending}
                    >
                      保存
                    </button>
                  </div>
                )}
              </div>
              {actorModels && actorModels.length > 0 && (
                <div className={styles.infoGroup}>
                  <span className={styles.infoLabel}>{brandConfig.labels.aiActor}</span>
                  <span className={styles.infoValueSmall}>
                    {actorModels.map((model) => model.display_name || model.name).join(', ')}
                  </span>
                </div>
              )}
              {'variants' in material && material.variants && material.variants.length > 0 && (
                <div className={styles.variants}>
                  <span className={styles.infoLabel}>派生バリエーション</span>
                  <ul>
                    {material.variants.map((variant) => (
                      <li key={variant.id}>
                        <span>{variant.aspect_ratio || '未指定'}</span>
                        <span>{variant.storage_path || '-'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </aside>
      )}
    </div>
  );
}
